/**
 * Authentication Context
 *
 * Provides global authentication state and functions for login, logout, register.
 * Automatically restores session from sessionStorage on mount.
 *
 * Multi-tenancy (v0.13.0): a session also has an ACTIVE TENANT. One login can hold
 * memberships of several tenants — an Akhile consultant working across clients — so the
 * context exposes the membership list and a `switchTenant` action. Switching mints a new
 * token server-side rather than re-scoping the existing one, so the token in sessionStorage
 * must be replaced, which is why `switchTenant` lives here rather than in a component.
 */

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import type {
  LoginResponse,
  RegisterResponse,
  TenantMembership,
  TenantRole,
  User,
} from '../types/auth';
import * as authAPI from '../api/auth';
import * as tenantAPI from '../api/tenants';
import * as permissions from '../utils/permissions';

// T028: AuthContext type definition

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (credential: string, password: string) => Promise<void>;
  logout: () => void;
  register: (username: string, email: string, password: string, full_name?: string) => Promise<RegisterResponse>;
  isAuthenticated: boolean;
  /**
   * @deprecated Ambiguous under multi-tenancy — it cannot say *which* kind of admin. Use
   * `isPlatformAdmin` for Akhile's back office or `isTenantAdmin` for authority inside the
   * active tenant. Kept so existing call sites keep compiling during the rollout.
   */
  isAdmin: boolean;
  /** Akhile back office: audit, email config, auth policy, tenant administration. */
  isPlatformAdmin: boolean;
  /** May invite people, change roles and manage seats within the active tenant. */
  isTenantAdmin: boolean;
  isPortfolioManager: boolean;
  /** Read-only within the active tenant — gates "create" affordances. */
  isTenantViewer: boolean;
  canManageProgrammes: boolean;
  canEditProject: (projectId: string, programmeId?: string) => boolean;
  canViewProject: (projectId: string, programmeId?: string) => boolean;
  // --- tenancy ---
  activeTenantId: string | null;
  tenantRole: TenantRole | null;
  memberships: TenantMembership[];
  hasMultipleTenants: boolean;
  /** True when the session has no tenant: every domain call will 400 until one is granted. */
  hasNoActiveTenant: boolean;
  switchTenant: (tenantId: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  // T029: Load user from sessionStorage on mount
  useEffect(() => {
    const loadUser = async () => {
      const storedToken = sessionStorage.getItem('token');
      if (storedToken) {
        try {
          // Validate token and fetch user profile. /auth/me returns the active tenant and
          // membership list too, so a refreshed page recovers its tenant context rather than
          // rendering as though the user belonged nowhere.
          const currentUser = await authAPI.getCurrentUser();
          setUser(currentUser);
          setToken(storedToken);
        } catch (error) {
          // Token invalid or expired - clear storage
          console.error('Failed to restore session:', error);
          sessionStorage.removeItem('token');
        }
      }
      setLoading(false);
    };

    loadUser();
  }, []);

  const login = async (credential: string, password: string): Promise<void> => {
    const response: LoginResponse = await authAPI.login(credential, password);

    // Store token in sessionStorage
    sessionStorage.setItem('token', response.access_token);

    // Update context state
    setToken(response.access_token);
    setUser(response.user);
  };

  const logout = (): void => {
    // Call logout API endpoint
    authAPI.logout().catch(err => {
      console.error('Logout API call failed:', err);
      // Continue with client-side logout even if API call fails
    });

    // Clear session storage
    sessionStorage.removeItem('token');

    // Reset context state
    setToken(null);
    setUser(null);
  };

  const register = async (
    username: string,
    email: string,
    password: string,
    full_name?: string
  ): Promise<RegisterResponse> => {
    const response: RegisterResponse = await authAPI.register({
      username,
      email,
      password,
      full_name
    });

    return response;
    // Note: Registration does NOT auto-login - user must be approved first (unless first user)
  };

  /**
   * Change which tenant this session acts in.
   *
   * The server re-verifies the membership and issues a fresh token; the old one stays bound
   * to the previous tenant, so it must be replaced rather than kept alongside.
   */
  const switchTenant = async (tenantId: string): Promise<void> => {
    const response = await tenantAPI.switchTenant(tenantId);
    sessionStorage.setItem('token', response.access_token);
    setToken(response.access_token);
    setUser(response.user);
  };

  // Computed properties
  const isAuthenticated = !!user && !!token;
  const isPlatformAdmin = permissions.isPlatformAdmin(user);
  const isTenantAdmin = permissions.isTenantAdmin(user);
  // Deprecated: true for either kind of admin, which is why it should not gate anything new.
  const isAdmin = isPlatformAdmin || isTenantAdmin;
  const isPortfolioManager = permissions.isPortfolioManager(user);
  const isTenantViewer = permissions.isTenantViewer(user);
  const canManageProgrammes = permissions.canManageProgrammes(user);

  const activeTenantId = user?.active_tenant_id ?? null;
  const tenantRole = user?.tenant_role ?? null;
  const memberships = user?.memberships ?? [];
  const hasMultipleTenants = permissions.hasMultipleTenants(user);
  const hasNoActiveTenant = permissions.hasNoActiveTenant(user);

  // Permission checking functions. Logic lives in utils/permissions so the
  // context and the usePermissions hook share one implementation. Backend
  // enforces actual permissions - these only drive UI visibility.
  const canEditProject = (projectId: string, programmeId?: string): boolean =>
    permissions.canEditProject(user, projectId, programmeId);

  const canViewProject = (projectId: string, programmeId?: string): boolean =>
    permissions.canViewProject(user, projectId, programmeId);

  const value: AuthContextType = {
    user,
    token,
    login,
    logout,
    register,
    isAuthenticated,
    isAdmin,
    isPlatformAdmin,
    isTenantAdmin,
    isPortfolioManager,
    isTenantViewer,
    canManageProgrammes,
    canEditProject,
    canViewProject,
    activeTenantId,
    tenantRole,
    memberships,
    hasMultipleTenants,
    hasNoActiveTenant,
    switchTenant
  };

  // Show loading spinner while restoring session
  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
      </div>
    );
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
